<template>
  <div class="modification-page">

    <!-- Loading -->
    <div v-if="loading" class="loading-spinner">
      <div class="spinner"></div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="message">
      <h2>Błąd</h2>
      <p>{{ error }}</p>
    </div>

    <!-- Already cancelled -->
    <div v-else-if="cancelled" class="message">
      <h2>Ta wizyta jest odwołana.</h2>
    </div>

    <!-- Confirmed after clicking -->
    <div v-else-if="confirmed" class="message">
      <h2>Wizyta została potwierdzona.</h2>
    </div>

    <!-- Too late to cancel -->
    <div v-else-if="tooLate" class="message">
      <h2>
        Został przekroczony czas do możliwego odwołania wizyty.
      </h2>

      <p>
        Skontaktuj się z rejestracją
      </p>

      <a
        href="tel:713591918"
        class="contact-btn"
      >
        Zadzwoń teraz
      </a>
    </div>

    <!-- Visit is over -->
    <div v-else-if="expired" class="message">
      <h2>Wizyta już się odbyła.</h2>
    </div>

    <!-- Visit -->
    <div v-else-if="visit">

      <h2>Twoja wizyta</h2>

      <div class="visit-info">

        <p>
          <strong>Data:</strong>
          {{ formattedVisitDate }}
        </p>

        <p>
          <strong>Godzina:</strong>
          {{ formattedVisitTime }}
        </p>

        <p>
          <strong>Lekarz:</strong>
          {{ visit.doctor }}
        </p>

        <p>
          <strong>Typ wizyty:</strong>
          {{ visit.visitType }}
        </p>

        <div class="actions">

          <!-- Already confirmed -->
          <p class="already-confirmed"
            v-if="alreadyConfirmed"
          >
            ✅ Wizyta została potwierdzona.
          </p>

          <!-- Confirmation button -->
          <button
            v-else-if="canConfirm"
            type="button"
            class="confirm-btn"
            @click="confirmVisit"
            :disabled="processing"
          >
            Potwierdź wizytę
          </button>

          <button
            type="button"
            class="change-date-btn"
          >
            Zmień termin wizyty
          </button>

          <button
            type="button"
            class="cancel-btn"
            @click="cancelVisit"
            :disabled="processing"
          >
            Odwołaj wizytę
          </button>

          <p>
            <strong>Skontaktuj się z rejestracją</strong>
          </p>

          <a
            href="tel:713591918"
            class="contact-btn"
          >
            Zadzwoń teraz
          </a>

        </div>

        <br>

        <p>
          <strong>Adres:</strong>
          Młodych Techników 7<br>
          53-647 Wrocław
        </p>

        <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2504.702704243271!2d17.009780377532508!3d51.11394513936904!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x470fc207e9147563%3A0x64db12be13f93a40!2sLiveDent%E2%84%A2%20Clinic%20-%20Centrum%20Implantologii%20i%20Stomatologii!5e0!3m2!1spl!2spl!4v1789734088436!5m2!1spl!2spl" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="strict-origin-when-cross-origin"></iframe>
      </div>
    </div>

    <!-- Success -->
    <div
      v-else-if="success"
      class="message"
    >
      <h2>Twoja wizyta została pomyślnie odwołana.</h2>
    </div>

  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "ModifyVisit",

  data() {
    return {
      loading: true,
      processing: false,

      error: null,
      cancelled: false,
      alreadyConfirmed: false,
      confirmed: false,
      success: false,

      canCancel: false,
      canConfirm: false,
      tooLate: false,

      visit: null,
      expired: false
    };
  },

  computed: {
    formattedVisitTime() {
      if (!this.visit?.date) return "";

      return this.visit.date.substring(11, 16);
    },

    formattedVisitDate() {
      if (!this.visit?.date) return "";

      return this.visit.date.substring(0, 10);
    }
  },

  methods: {
    getToken() {
      return new URLSearchParams(
        window.location.search
      ).get("token");
    },

    async fetchVisit() {
      const token = this.getToken();

      if (!token) {
        this.error = "Nieprawidłowy link.";
        this.loading = false;
        return;
      }

      try {
        const res = await axios.get(
          `${process.env.VUE_APP_API_URL}/api/modifyVisit`,
          {
            params: {
              token
            }
          }
        );

        if (res.data.cancelled) {
          this.cancelled = true;
          return;
        }

        if (res.data.expired) {
          this.expired = true;
          return;
        }

        this.visit = res.data.visit;
        this.canCancel = res.data.canCancel;
        this.canConfirm = res.data.canConfirm;
        this.alreadyConfirmed = res.data.alreadyConfirmed;

      } catch (err) {
        console.error("Error loading visit:", err);

        this.error =
          err.response?.data?.error ||
          "Nie udało się pobrać informacji o wizycie.";
      } finally {
        this.loading = false;
      }
    },

    async cancelVisit() {
      const token = this.getToken();

      if (!token || this.processing) {
        return;
      }

      if (!this.canCancel) {
        this.tooLate = true;
        return;
      }

      const confirmed = window.confirm(
        "Czy na pewno chcesz odwołać wizytę?"
      );

      if (!confirmed) {
        return;
      }

      this.processing = true;

      try {
        const res = await axios.post(
          `${process.env.VUE_APP_API_URL}/api/modifyVisit`,
          {
            token,
            action: "cancel"
          }
        );

        if (res.data.alreadyCanceled || res.data.cancelled) {
          this.cancelled = true;
          this.visit = null;
          return;
        }

        if (res.data.success) {
          this.success = true;
          this.visit = null;
        }

      } catch (err) {
        console.error("Error cancelling visit:", err);

        this.error =
          err.response?.data?.error ||
          "Nie udało się odwołać wizyty.";

        this.visit = null;

      } finally {
        this.processing = false;
      }
    },

    async confirmVisit() {
      const token = this.getToken();

      if (!token || this.processing) {
        return;
      }

      if (!this.canConfirm) {
        return;
      }

      this.processing = true;

      try {
        const res = await axios.post(
          `${process.env.VUE_APP_API_URL}/api/modifyVisit`,
          {
            token,
            action: "confirm"
          }
        );

        if (res.data.alreadyConfirmed) {
          this.alreadyConfirmed = true;
          return;
        }

        if (res.data.confirmed) {
          this.confirmed = true;
          this.visit = null;
        }

      } catch (err) {
        console.error("Error confirming visit:", err);

        this.error =
          err.response?.data?.error ||
          "Nie udało się potwierdzić wizyty.";

        this.visit = null;

      } finally {
        this.processing = false;
      }
    }
  },

  mounted() {
    this.fetchVisit();
  }
};
</script>

<style scoped>
.modification-page {
  padding: 2rem;
  max-width: 600px;
  margin: 0 auto;
}

.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem 0;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #007bff;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.visit-info {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 2rem 1rem 3rem 1rem;
  margin: 3rem 0;
}

.visit-info p {
  margin: 0.75rem 0;
}

.actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

.actions button,
.actions .contact-btn {
  width: 55%;
  min-height: 3rem;
  box-sizing: border-box;
  padding: 0.75rem;
  border: none;
  border-radius: 2rem;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
}

.confirm-btn {
  background: #764c9e;
  color: white;
}

.confirm-btn:hover {
  background: #69468b;
}

.cancel-btn {
  background: #e8e8e8;
  color: #8a8a8a;
  margin-bottom: 1rem;
}

.cancel-btn:hover {
  background: #b9b9b9;
}

.change-date-btn {
  background: #bd90f0;
  color: white;
}

.change-date-btn:hover {
  background: #9f78ca;
}

.contact-btn {
  background: #8dc254;
  color: white;
}

.contact-btn:hover {
  background: #7ba948;
}

.actions button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.message {
  text-align: center;
  padding: 2rem 0;
}

.message .contact-btn {
  display: block;
  width: 100%;
  box-sizing: border-box;
  padding: 0.75rem;
  border-radius: 6px;
  text-decoration: none;
  margin-top: 1rem;
}

iframe {
  width: 65%;
  height: 25rem;
}

p.already-confirmed {
  color: #307e2d;
}

@media (max-width: 768px) {
  iframe {
    width: 100%;
  }

  .actions button,
  .actions .contact-btn,
  .actions p {
    width: 100%;
  }
}
</style>